import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';

export const generateCopyStringBody = ({
  requiredDocs,
  optionalDocs,
  includeInstructions = false,
}: {
  requiredDocs?: TOfferDataRequirement['actions'];
  optionalDocs?: TOfferDataRequirement['actions'];
  includeInstructions?: boolean;
}) => {
  let copyStringBodyText: string = '';
  if (requiredDocs) {
    copyStringBodyText = generateBodyTextString({
      docRequirements: requiredDocs,
      newLineBreak: '\n',
      includeInstructions,
    });
  }

  if (optionalDocs && optionalDocs.length > 0) {
    copyStringBodyText = `${copyStringBodyText}${generateBodyTextString({
      docRequirements: optionalDocs,
      newLineBreak: '\n',
      includeInstructions,
    })}`;
  }

  return copyStringBodyText;
};

export const generateBodyTextString = ({
  docRequirements,
  newLineBreak,
  includeInstructions = false,
}: {
  docRequirements: TOfferDataRequirement['actions'];
  newLineBreak: string;
  includeInstructions?: boolean;
}) => {
  const text = docRequirements.map((requirement) => {
    const title = requirementsTitle(
      requirement.type,
      requirement.quantifier?.value,
    );

    if (!title) return '';

    const titleLine = `- ${title} ${
      requirement.is_optional ? '(optional)' : ''
    }${newLineBreak}`;

    if (!includeInstructions) {
      return titleLine;
    }

    const instruction = renderInstructionText(requirement.type);

    return instruction
      ? `${titleLine}${instruction}${newLineBreak}`
      : titleLine;
  });

  return text.join('');
};

export const generateFooterText = (newLineBreak: string) => {
  const bullets = [
    t`You send the data in PDF format`,
    t`No file is larger than 50 MB`,
    t`The files are not password protected (or share the password with us)`,
  ];

  return `${t`Please make sure that...`}${newLineBreak}${bullets
    .map((bullet) => `- ${bullet}`)
    .join(newLineBreak)}`;
};

export const requirementsTitle = (
  type: string | undefined,
  quantifier: number | undefined,
) => {
  switch (type) {
    case 'company_accounts':
      return 'Most recent filed company accounts';
    case 'bank_statement':
      if (quantifier) {
        return `Last ${quantifier} months of bank statements as PDF files`;
      }
      return 'Last bank statements as PDF files';
    case 'vat_return': {
      const quarter = quantifier && quantifier / 3;
      if (quarter && quarter > 1) {
        return `Last ${quantifier} quarterly VAT returns`;
      }
      return `Last quarter's VAT return`;
    }
    case 'link_open_banking':
      return 'Link your trading bank account';
    case 'via_open_banking':
      return 'Link your business bank account';
    case 'personal_bank_statement':
      return 'Last 3 months personal bank statements';
    case 'management_accounts':
      return 'Current management accounts';
    case 'business_plan':
      return 'Business plan';
    case 'aged_debtor_report':
      return 'Aged debtor report';
    case 'kontoauszuege_hochladen':
      return t`Bank statements`;
    case 'bwa_hochladen':
      return t`Current BWA and SuSa`;
    case 'bwa_susa_hochladen': {
      const notOlderThan = t`not older than 4 months`;
      return t`Current BWA and SuSa (${notOlderThan})`;
    }
    case 'gewerbeschein_hochladen':
      return t`Trade certificate`;
    case 'jaehrliche_bwa_susa_hochladen':
      if (quantifier && quantifier > 1) {
        return t`BWAs and SuSas from the last ${quantifier} years`;
      }
      return t`BWAs and SuSas from the last year`;
    case 'gesellschaftsvertrag_hochladen':
      return t`Articles of association`;
    case 'aktienregisterauszug_hochladen':
      return t`Share registry`;
    default:
      break;
  }
};

const BANK_STATEMENTS_TIPS_URL =
  'https://support.iwoca.de/de/articles/12062223-kontoauszuge-tipps-damit-es-auf-anhieb-klappt';
const SUSA_TIPS_URL = 'https://www.iwoca.de/kredit/datev-guide';

// Plain-text instructions for the copy/email body. A clipboard/mailto body
// cannot hold a hyperlink, so the tip link renders as a literal URL
// ("... finden Sie hier: https://...") that mail clients auto-linkify.
const renderInstructionText = (type: string | undefined) => {
  switch (type) {
    case 'jaehrliche_bwa_susa_hochladen':
      return `${t`All BWAs should show company name, sales revenue, purchase of goods, costs including depreciation, interest expenses & preliminary result. All SuSas should include EB value, cumulative annual values & balance and be submitted in the correct format.`} ${t`Tips for getting the SuSa right first time can be found here: ${SUSA_TIPS_URL}`}`;
    case 'kontoauszuege_hochladen':
      return `${t`Download the full account statements from your online banking.`} ${t`Tips for getting the bank statements right first time can be found here: ${BANK_STATEMENTS_TIPS_URL}`}`;
    default:
      return '';
  }
};

// JSX instructions for the on-page accordion. The tip "here" renders as a
// clickable anchor, matching the document upload cards.
export const renderInstruction = (type: string | undefined) => {
  switch (type) {
    case 'jaehrliche_bwa_susa_hochladen':
      return (
        <Trans>
          All BWAs should show company name, sales revenue, purchase of goods,
          costs including depreciation, interest expenses & preliminary result.
          All SuSas should include EB value, cumulative annual values & balance
          and be submitted in the correct format. Tips for getting the SuSa
          right first time can be found{' '}
          <a href={SUSA_TIPS_URL} target="_blank" rel="noreferrer">
            here
          </a>
          .
        </Trans>
      );
    case 'kontoauszuege_hochladen':
      return (
        <Trans>
          Download the full account statements from your online banking. Tips
          for getting the bank statements right first time can be found{' '}
          <a href={BANK_STATEMENTS_TIPS_URL} target="_blank" rel="noreferrer">
            here
          </a>
          .
        </Trans>
      );
    default:
      return undefined;
  }
};
